import { useCallback, useEffect, useRef, useState } from "react";
import { useSettings } from "../contexts/SettingsContext";
import { useToast } from "../contexts/ToastContext";
import { useBackend } from "../lib/backend";
import { activeInstrumentFromSettings } from "../lib/instruments";
import type { StickyBulletinData } from "../lib/services/editor";

export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export type SectionId = "inbox" | "volwatch" | "notes" | "upcoming";

export interface HotTimeEntry {
  bucket: string;
  dayOfWeek?: number;
  count: number;
  instruments: string[];
}

export function useStickyBulletin(
  open: boolean,
  anchorRef: React.RefObject<HTMLButtonElement | null>,
) {
  const backend = useBackend();
  const { addToast } = useToast();
  const { selectedInstrument, selectedSymbol } = useSettings();
  const activeInstrument = activeInstrumentFromSettings({
    selectedInstrument,
    selectedSymbol,
  });
  const [tradingNotes, setTradingNotes] = useState("");
  const [volWatchTimes, setVolWatchTimes] = useState<
    StickyBulletinData["volWatchTimes"]
  >([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showHotTimes, setShowHotTimes] = useState(false);
  const [hotTimesLoaded, setHotTimesLoaded] = useState(false);
  const [hotTimesByDay, setHotTimesByDay] = useState(false);
  const [hotTimes, setHotTimes] = useState<HotTimeEntry[]>([]);
  const [showQuickClock, setShowQuickClock] = useState(true);
  const [quickClockPulse, setQuickClockPulse] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionId>("upcoming");
  const [editingNotes, setEditingNotes] = useState(false);
  const [newVolWatchTime, setNewVolWatchTime] = useState("");
  const [newVolWatchDay, setNewVolWatchDay] = useState(new Date().getDay());
  const [newVolWatchInstrument, setNewVolWatchInstrument] = useState("NQ");
  const [newVolWatchNotes, setNewVolWatchNotes] = useState("");
  const notesSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [popupPos, setPopupPos] = useState<{
    top: number;
    right: number;
  } | null>(null);

  useEffect(() => {
    if (!open || !anchorRef.current) {
      setPopupPos(null);
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    setPopupPos({
      top: rect.bottom + 8,
      right: Math.max(window.innerWidth - rect.right, 12),
    });
  }, [anchorRef, open]);

  useEffect(() => {
    if (!open || hasLoaded) return;
    backend.stickyBulletin
      .get()
      .then((response) => {
        setTradingNotes(response.data.tradingNotes);
        setVolWatchTimes(response.data.volWatchTimes);
      })
      .finally(() => setHasLoaded(true));
  }, [backend.stickyBulletin, hasLoaded, open]);

  useEffect(() => {
    if (!showHotTimes) return;
    setHotTimesLoaded(false);
    backend.stickyBulletin
      .getVolWatchHotTimes(hotTimesByDay)
      .then((response) => setHotTimes(response.hotTimes))
      .finally(() => setHotTimesLoaded(true));
  }, [backend.stickyBulletin, hotTimesByDay, showHotTimes]);

  useEffect(
    () => () => {
      if (notesSaveTimer.current) clearTimeout(notesSaveTimer.current);
    },
    [],
  );

  const saveNotes = useCallback(
    (value: string) => {
      if (notesSaveTimer.current) clearTimeout(notesSaveTimer.current);
      notesSaveTimer.current = setTimeout(() => {
        void backend.stickyBulletin.save({ tradingNotes: value });
      }, 1200);
    },
    [backend.stickyBulletin],
  );

  function handleNotesChange(value: string) {
    setTradingNotes(value);
    saveNotes(value);
  }

  async function handleAddVolWatch() {
    if (!newVolWatchTime) {
      addToast("Enter a time", "error");
      return;
    }
    try {
      await backend.stickyBulletin.addVolWatchTime({
        time: newVolWatchTime,
        dayOfWeek: newVolWatchDay,
        instrument: newVolWatchInstrument,
        notes: newVolWatchNotes,
      });
      setVolWatchTimes((times) => [
        {
          time: newVolWatchTime,
          dayOfWeek: newVolWatchDay,
          instrument: newVolWatchInstrument,
          notes: newVolWatchNotes,
          createdAt: new Date().toISOString(),
        },
        ...times,
      ]);
      setNewVolWatchTime("");
      setNewVolWatchNotes("");
      addToast(
        "VolWatch observation logged",
        "success",
        undefined,
        undefined,
        "bottom-left",
      );
    } catch {
      addToast("VolWatch save failed", "error");
    }
  }

  async function handleQuickClock() {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    const dayOfWeek = now.getDay();
    setQuickClockPulse(true);
    window.setTimeout(() => setQuickClockPulse(false), 600);
    try {
      await backend.stickyBulletin.addVolWatchTime({
        time,
        dayOfWeek,
        instrument: activeInstrument,
        notes: "",
      });
      setVolWatchTimes((times) => [
        {
          time,
          dayOfWeek,
          instrument: activeInstrument,
          notes: "",
          createdAt: now.toISOString(),
        },
        ...times,
      ]);
      addToast(
        `${time} ${DAY_LABELS[dayOfWeek]} ${activeInstrument}`,
        "success",
        "VolWatch clocked",
        undefined,
        "bottom-left",
      );
    } catch {
      addToast("VolWatch log failed", "error");
    }
  }

  return {
    popupPos,
    panelRef,
    activeSection,
    setActiveSection,
    volWatchTimes,
    newVolWatchTime,
    setNewVolWatchTime,
    newVolWatchDay,
    setNewVolWatchDay,
    newVolWatchInstrument,
    setNewVolWatchInstrument,
    newVolWatchNotes,
    setNewVolWatchNotes,
    handleAddVolWatch,
    showQuickClock,
    setShowQuickClock,
    quickClockPulse,
    handleQuickClock,
    showHotTimes,
    setShowHotTimes,
    hotTimesLoaded,
    hotTimesByDay,
    setHotTimesByDay,
    hotTimes,
    tradingNotes,
    editingNotes,
    setEditingNotes,
    handleNotesChange,
    selectedSymbol,
  };
}
